<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="max-w-full">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                {{ this.breakdown.figure_type }} Revenue Breakdown
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="py-6 px-4 sm:px-6">
                                        <div class="flex gap-4 shadow border-b border-gray-200 w-full">
                                            <div class="overflow-x-auto">
                                                <table class="min-w-full table-fixed divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Supplier</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Fixture</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Fixture Reference</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Quote</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Service Line</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Buy Total</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Sell Total</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Commission</th>
                                                            <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Management Fee</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(item,index) in this.breakdown.items"
                                                            :key="index"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <a :href="route('frontend.organisations.suppliers.organisation.show' , item.supplier_id)">
                                                                    {{ item.supplier_name }}
                                                                </a>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <a :href="route('frontend.fixture.show' , item.fixture_id)">
                                                                    {{ item.fixture_title }}
                                                                </a>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <a :href="route('frontend.fixture.show' , item.fixture_id)">
                                                                    {{ item.fixture_reference }}
                                                                </a>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <a :href="route('frontend.fixture.quote.show' , [item.fixture_id, item.quote_id])">
                                                                    {{ item.quote_name }}
                                                                </a>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <a :href="route('frontend.fixture.quote.show' , [item.fixture_id, item.quote_id])">
                                                                    {{ item.service_name }} 
                                                                </a>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ formatCurrency(item.buy_total, item.buy_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ formatCurrency(item.sell_total, item.sell_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ formatCurrency(item.commission, item.sell_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ formatCurrency(item.management_fee, item.sell_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                        </tr>
                                                        <tr class="bg-gray-300">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold" colspan="5">
                                                                Totals:
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                {{ formatCurrency(this.breakdown.buy_total, this.breakdown.buy_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                {{ formatCurrency(this.breakdown.sell_total, this.breakdown.sell_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                {{ formatCurrency(this.breakdown.commission_total, this.breakdown.sell_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                {{ formatCurrency(this.breakdown.management_fee_total, this.breakdown.sell_currency?.code ?? 'gbp', true) }}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, computed} from 'vue';
import {emitter} from "@/eventHub";
import {usePage} from '@inertiajs/vue3';
import {XIcon} from '@heroicons/vue/outline';
import SlideOutErrors from "@/Components/SlideOutErrors";
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'



export default {
    components: {
        XIcon,
        Dialog,
        DialogTitle,
        DialogOverlay,
        SlideOutErrors,
        TransitionRoot,
        TransitionChild
    },
    props: ['breakdown'],
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            breakdown: null
        }
    },
    methods: {
        open(breakdown) {
            this.show = true;
            this.breakdown = breakdown;
        },
        close() {
            this.show = false;
        }
    },
}
</script>
